<template>
  <div>
    <PageAllow controller="SubAccountAnalyses" action="view" />
    <Breadcrumbs :items="items" />

    <v-sheet class="overflow-y-scroll fill-content-height">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="isSplit"
              small
              color="accent"
              @click.stop="dialog = true"
            >
              <v-icon x-small class="pe-1">fa-money-check</v-icon>
              {{
                "EXTYISHENG" == subAccount.bank_code
                  ? $t("SubAccountAnalyses.splitAndWithdraw")
                  : $t("SubAccountAnalyses.split")
              }}
            </v-btn>
            <v-btn
              v-if="
                !isSplit &&
                $permission.permissionAllow('SubAccounts', 'exportDetail')
              "
              small
              class="ms-2"
              color="print"
              dark
              @click.stop="exportExcel()"
            >
              <v-icon x-small class="pe-1">fa-file-excel</v-icon>
              {{ $t("Export.excel", [$t("Nav.subAccountAnalysesDetails")]) }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.pay_flgs"
                  :label="$t('SubAccountAnalyses.payFlags')"
                  :items="payFlgsItems"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="4"
                v-if="settings && !settings.ws_sub_account_enable"
              >
                <SearcherStatus
                  v-model="options.order_status"
                  :label="$t('SubAccountAnalyses.orderStatus')"
                  :items="orderStatusItems"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.sub_account_status"
                  :label="$t('SubAccountAnalyses.subAccountStatus')"
                  :items="subAccountStatusItems"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div class="mx-4 mb-4">
        <v-data-table
          v-model="selectedSubAccountDetails"
          :show-select="isSplit"
          :headers="headers"
          :items="subAccountAnalysesDetails"
          item-key="id"
          :options.sync="options"
          :server-items-length="paging.count"
          :loading="loading"
          multi-sort
          class="elevation-1 px-4 pt-1"
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:top>
            <v-card-text>
              <v-row dense>
                <v-col cols="12" sm="6" md="3">
                  <span class="font-weight-bold me-2">{{
                    $t("SubAccountAnalyses.DetailInfo.accountNumber")
                  }}</span>
                  <span>{{ subAccount.account_number }}</span>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <span class="font-weight-bold me-2">{{
                    $t("SubAccountAnalyses.DetailInfo.accountName")
                  }}</span>
                  <span>{{ subAccount.account_name }}</span>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <span class="font-weight-bold me-2">{{
                    $t("SubAccountAnalyses.DetailInfo.subAccountType")
                  }}</span>
                  <span>{{
                    subAccount.sub_account_type
                      ? subAccount.sub_account_type +
                        "-" +
                        subAccount.sub_account_type_name
                      : ""
                  }}</span>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <span class="font-weight-bold me-2">{{
                    $t("SubAccountAnalyses.DetailInfo.bank")
                  }}</span>
                  <span>{{ subAccount.bank_name }}</span>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
          </template>
          <template v-slot:body.prepend>
            <tr class="d-none d-sm-table-row">
              <td v-if="isSplit"></td>
              <td colspan="4">
                <span>{{ $t("TableSum") }}</span>
              </td>
              <td :align="$vuetify.rtl ? 'left' : 'right'">
                <span
                  >{{ $t("Global.money") }}
                  {{ totalReceiveMoney | formatMoney }}</span
                >
              </td>
              <td :align="$vuetify.rtl ? 'left' : 'right'">
                <span
                  >{{ $t("Global.money") }}
                  {{ totalSubAccountMoney | formatMoney }}</span
                >
              </td>
              <td colspan="3"></td>
            </tr>
            <tr class="d-table-row d-sm-none">
              <td class="v-data-table__mobile-row">
                <div class="v-data-table__mobile-row__header">
                  {{ $t("TableSum") }}
                </div>
                <div class="v-data-table__mobile-row__cell"></div>
              </td>
              <td class="v-data-table__mobile-row">
                <div class="v-data-table__mobile-row__header">
                  {{ $t("SubAccountAnalyses.DetailTableHeaders.receiveMoney") }}
                </div>
                <div class="v-data-table__mobile-row__cell">
                  {{ $t("Global.money") }} {{ totalReceiveMoney | formatMoney }}
                </div>
              </td>
              <td class="v-data-table__mobile-row">
                <div class="v-data-table__mobile-row__header">
                  {{
                    $t("SubAccountAnalyses.DetailTableHeaders.subAccountMoney")
                  }}
                </div>
                <div class="v-data-table__mobile-row__cell">
                  {{ $t("Global.money") }}
                  {{ totalSubAccountMoney | formatMoney }}
                </div>
              </td>
            </tr>
          </template>

          <template v-slot:item.shop_name="{ item }">
            {{ item.shop_name }}<br />
            <span class="caption grey--text">
              {{ item.shop_code }}
            </span>
          </template>
          <template v-slot:item.bill_code="{ item }">
            {{ item.bill_code }}<br />
            <v-chip
              v-if="item.bill_code"
              small
              label
              class="me-1"
              :color="item.bill_pay_flg | billPayFlgsColor"
              :text-color="item.bill_pay_flg | billPayFlgsTextColor"
              >{{ item.bill_pay_flg | billPayFlgsText }}
            </v-chip>
            <v-chip
              v-if="item.bill_code"
              small
              label
              class="px-2"
              :color="item.bill_status | billStatusColor"
              :text-color="item.bill_status | billStatusTextColor"
              >{{ item.bill_status | billStatusText }}
            </v-chip>
          </template>
          <template v-slot:item.order_code="{ item }">
            {{ item.order_code }}<br />
            <v-chip
              v-if="item.order_code"
              small
              label
              class="me-1"
              :color="item.order_status | orderStatusColor"
              :text-color="item.order_status | orderStatusTextColor"
              >{{ item.order_status | orderStatusText }}
            </v-chip>
            <v-chip
              v-if="item.order_code && item.order_cancel_flg"
              small
              label
              outlined
              class="px-2"
              color="danger"
              >{{ $t("CRUD.cancelled") }}
            </v-chip>
          </template>
          <template v-slot:item.receive_money="{ item }">
            {{ $t("Global.money") }}
            {{ item.receive_money | formatMoney }}
          </template>
          <template v-slot:item.sub_account_money="{ item }">
            {{ $t("Global.money") }}
            {{ item.sub_account_money | formatMoney }}
          </template>
          <template v-slot:item.sub_account_status="{ item }">
            <v-chip
              small
              label
              :color="item.sub_account_status | subAccountStatusColor"
              :text-color="item.sub_account_status | subAccountStatusTextColor"
            >
              {{ item.sub_account_status | subAccountStatusText }}
            </v-chip>
          </template>
          <template v-slot:item.payment_time="{ item }">
            <div
              class="caption grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.payment_time | formatTime }}
            </div>
          </template>
          <template v-slot:item.sub_account_time="{ item }">
            <div
              class="caption grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.sub_account_time | formatTime }}<br />
              {{
                settings &&
                settings.push_platform == 1018 &&
                item.sub_account_bank_date
                  ? $t("SubAccountAnalyses.kingdeeVoucherPushed")
                  : ""
              }}
            </div>
          </template>
        </v-data-table>
      </div>

      <v-dialog v-model="dialog" max-width="360px">
        <v-card>
          <v-card-title @click="dialog = false">
            <span class="headline">{{ $t("SubAccountAnalyses.split") }}</span>
            <v-spacer></v-spacer>
            <v-icon @click="dialog = false">fa-times</v-icon>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="5" class="font-weight-bold text-right">{{
                  $t("SubAccountAnalyses.splitCount")
                }}</v-col>
                <v-col cols="7">
                  {{ splitCount }}
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="5" class="font-weight-bold text-right">{{
                  $t("SubAccountAnalyses.splitTotal")
                }}</v-col>
                <v-col cols="7">
                  {{ $t("Global.money") }}
                  {{ splitMoney | formatMoney }}
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="5" class="font-weight-bold text-right">{{
                  $t("SubAccountAnalyses.splitAccount")
                }}</v-col>
                <v-col cols="7">{{ subAccount.account_number }}</v-col>
              </v-row>
              <v-row>
                <v-col cols="5" class="font-weight-bold text-right">{{
                  $t("SubAccountAnalyses.splitBank")
                }}</v-col>
                <v-col cols="7">{{ subAccount.bank_name }}</v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              v-if="
                isSplit && $permission.permissionAllow('SubAccounts', 'split')
              "
              color="accent"
              @click.stop="split()"
            >
              <v-icon x-small class="pe-1">fa-money-check</v-icon>
              {{
                "EXTYISHENG" == subAccount.bank_code
                  ? $t("SubAccountAnalyses.splitAndWithdraw")
                  : $t("SubAccountAnalyses.split")
              }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-sheet>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SubAccounts from "@/api/sub_accounts";
import SubAccountAnalyses from "@/api/sub_account_analyses";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";

const defaultOptions = {
  datetimeRadioSelected: "receive",
  receive_time_from:
    new Date(new Date().getTime() - 31 * 24 * 3600 * 1000) // 从昨天算起再往前三十天内
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  receive_time_to:
    new Date(new Date().getTime() - 1 * 24 * 3600 * 1000) // 截止昨天
      .toISOString()
      .substr(0, 10) + " 23:59:59",
  sub_account_time_from: "",
  sub_account_time_to: "",
  order_time_from: "",
  order_time_to: "",
  expect_time_from: "",
  expect_time_to: "",
  sortBy: [],
  sortDesc: [],
  pay_flgs: [3], //账单支付状态
  order_status: [6], //订单状态
  sub_account_status: [0, 2],
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherStatus,
    SearcherDatetimeRangeWithRadio,
  },

  computed: {
    isSplit() {
      return this.$route.path.startsWith("/sub-account-split/");
    },
    items() {
      var items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.subAccountAnalyses"),
          disabled: false,
          href: "/sub-account-analyses",
        },
      ];

      if (this.isSplit) {
        items.push({
          text: i18n.t("Nav.subAccountSplit"),
          disabled: true,
          href: "/",
        });
      } else {
        items.push({
          text: i18n.t("Nav.subAccountAnalysesDetails"),
          disabled: true,
          href: "/",
        });
      }

      return items;
    },
    headers: () => [
      {
        text: i18n.t("SubAccountAnalyses.DetailTableHeaders.voucherCode"),
        align: "start",
        sortable: false,
        value: "voucher_code",
      },
      {
        text: i18n.t("SubAccountAnalyses.DetailTableHeaders.shop"),
        align: "start",
        sortable: false,
        value: "shop_name",
      },
      {
        text: i18n.t("SubAccountAnalyses.DetailTableHeaders.bill"),
        align: "start",
        sortable: false,
        value: "bill_code",
      },
      {
        text: i18n.t("SubAccountAnalyses.DetailTableHeaders.order"),
        align: "start",
        sortable: false,
        value: "order_code",
      },
      {
        text: i18n.t("SubAccountAnalyses.DetailTableHeaders.receiveMoney"),
        align: "end",
        sortable: false,
        value: "receive_money",
      },
      {
        text: i18n.t("SubAccountAnalyses.DetailTableHeaders.subAccountMoney"),
        align: "end",
        sortable: false,
        value: "sub_account_money",
      },
      {
        text: i18n.t("SubAccountAnalyses.DetailTableHeaders.subAccountStatus"),
        align: "center",
        sortable: false,
        value: "sub_account_status",
      },
      {
        text: i18n.t("SubAccountAnalyses.DetailTableHeaders.receiveTime"),
        align: "start",
        sortable: false,
        value: "payment_time",
      },
      {
        text: i18n.t("SubAccountAnalyses.DetailTableHeaders.subAccountTime"),
        align: "start",
        sortable: false,
        value: "sub_account_time",
      },
    ],
    selectedReceiveMoney() {
      var sum = 0;
      this.selectedSubAccountDetails.forEach((subAccountDetail) => {
        sum += parseFloat(subAccountDetail.receive_money);
      });
      return sum;
    },
    splitMoney() {
      return this.selectedSubAccountDetails &&
        this.selectedSubAccountDetails.length > 0
        ? this.selectedReceiveMoney
        : this.totalReceiveMoney;
    },
    splitCount() {
      return this.selectedSubAccountDetails &&
        this.selectedSubAccountDetails.length > 0
        ? this.selectedSubAccountDetails.length
        : this.paging.count;
    },
    payFlgsItems() {
      return this.$consts.subAccountAnalyses.pay_flgs;
    },
    orderStatusItems() {
      return this.$consts.subAccountAnalyses.status;
    },
    datetimeRadios: () => [
      {
        key: "receive",
        text: i18n.t("SubAccountAnalyses.receive_time"),
      },
      {
        key: "sub_account",
        text: i18n.t("SubAccountAnalyses.sub_account_time"),
      },
      {
        key: "order",
        text: i18n.t("SubAccountAnalyses.order_time"),
      },
      {
        key: "expect",
        text: i18n.t("SubAccountAnalyses.expect_date"),
      },
    ],
    subAccountStatusItems() {
      return this.$consts.subAccountAnalyses.sub_account_status;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings"]),
  },

  mounted() {
    this.$store.commit("searchDistrict", false);
    this.getSubAccountFromAPI();
    this.$commonUtils.loadOptions("sub-account-analyses").then(
      function (data) {
        if (data.options) {
          this.options = data.options;
        } else this.resetSearch();
      }.bind(this)
    );
  },

  data: () => ({
    options: JSON.parse(JSON.stringify(defaultOptions)),
    paging: {},
    subAccount: {},
    selectedSubAccountDetails: [],
    subAccountAnalysesDetails: [],
    totalReceiveMoney: 0,
    totalSubAccountMoney: 0,
    totalCount: 0,
    totalShopCount: 0,
    dialog: false,
  }),

  methods: {
    resetSearch() {
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    onSearch() {
      this.getSubAccountAnalysesFromAPI();
    },
    getSubAccountFromAPI() {
      SubAccounts.fetchOne(this.$route.params.sub_account_id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.subAccount = response.data.sub_account;
          }
        }.bind(this)
      );
    },
    getSubAccountAnalysesFromAPI() {
      if (this.settings && this.settings.ws_sub_account_enable == 1) {
        delete this.options.order_status;
      }
      SubAccountAnalyses.fetchOne(
        this.$route.params.sub_account_id,
        Object.assign({}, this.options, {
          split: this.isSplit ? 1 : 0,
        })
      ).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.subAccountAnalysesDetails =
              response.data.sub_account_details.map((detail) => {
                detail.order_status = detail.order_status
                  ? Number(detail.order_status)
                  : 0;
                detail.order_cancel_flg = detail.order_cancel_flg
                  ? Number(detail.order_cancel_flg)
                  : 0;
                return detail;
              });
            this.totalReceiveMoney = response.data.total_receive_money;
            this.totalSubAccountMoney = response.data.total_sub_account_money;
            this.totalCount = response.data.total_count;
            this.totalShopCount = response.data.total_shop_count;
            this.paging = response.data.paging;
          }
        }.bind(this)
      );
    },
    exportExcel() {
      if (this.settings && this.settings.ws_sub_account_enable == 1) {
        delete this.options.order_status;
      }
      SubAccountAnalyses.exportOne(
        this.$route.params.sub_account_id,
        this.options
      );
    },
    split() {
      if (this.$store.fullscreenLoading) return; //防重复触发
      if (this.settings && this.settings.ws_sub_account_enable == 1) {
        delete this.options.order_status;
      }
      this.$store.dispatch("fullscreenLoading");
      this.dialog = false;
      SubAccounts.split(
        this.$route.params.sub_account_id,
        Object.assign({}, this.options, {
          split_count: this.splitCount,
          split_money: this.splitMoney,
          sub_account_details: this.selectedSubAccountDetails.map(
            (subAccountDetail) => {
              return {
                id: subAccountDetail.id,
                voucher_code: subAccountDetail.voucher_code,
              };
            }
          ),
        })
      ).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.$router.push("/sub-account-analyses");
            this.$snackbar.success(i18n.t("SubAccountAnalyses.splitQueued"));
          }
          this.$store.dispatch("fullscreenLoadingStop");
        }.bind(this)
      );
    },
  },

  watch: {
    // districtID: {
    //   handler() {
    //     this.getSubAccountAnalysesFromAPI();
    //   },
    // },
    settings: {
      handler() {
        if (this.settings && this.settings.ws_sub_account_enable == 1) {
          this.options = Object.assign({}, this.options, { order_status: [] });
        }
      },
    },
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getSubAccountAnalysesFromAPI();
        sessionStorage.setItem(
          "sub-account-analyses-options",
          JSON.stringify(this.options)
        );
      },
    },
  },

  beforeDestroy() {
    this.$store.dispatch("threadLoadingStopAll");
  },
};
</script>